import { HttpErrorResponse, HttpResponse, type HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, tap, throwError } from 'rxjs';

import { API_MESSAGES, isApiResponse } from '@core/api/api-response.model';
import { SKIP_ERROR_TOAST } from '@core/api/http-context.tokens';
import { ToastService } from '@core/notification/toast.service';

/** Exposed via CORS specifically so the UI can quote it back on a 500. */
export const CORRELATION_ID_HEADER = 'X-Correlation-Id';

/** RFC 6585 - how long to wait before retrying a throttled request. */
export const RETRY_AFTER_HEADER = 'Retry-After';

/**
 * LAST in the chain, and that position is load-bearing: sitting innermost
 * relative to auth means it observes the FINAL outcome exactly once. A 401
 * that the auth interceptor healed by refreshing and retrying never reaches
 * here as a failure, so the user is not told their session expired a moment
 * before the data arrives.
 *
 * Two distinct failure shapes, both handled:
 *  - transport / status failures, via the status map below;
 *  - HTTP 200 with `success: false`, the write-procedure business rejection,
 *    which is NOT a transport failure and is toasted as a warning.
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const silent = req.context.get(SKIP_ERROR_TOAST);
  const toast = inject(ToastService);

  return next(req).pipe(
    tap((event) => {
      if (silent || !(event instanceof HttpResponse)) return;

      const body: unknown = event.body;
      if (!isApiResponse(body) || body.success) return;

      // "Data Not found..!" is the contract's EMPTY RESULT message, not a
      // rejection. Toasting it would fire a warning every time a filter
      // matched nothing, which trains users to ignore the toast entirely.
      if (body.message === API_MESSAGES.notFound) return;

      // One tap per response event, and HttpClient emits exactly one
      // HttpResponse per request - so exactly one toast.
      toast.warn('Not completed', body.message || API_MESSAGES.failure);
    }),

    catchError((error: unknown) => {
      if (!silent && error instanceof HttpErrorResponse && shouldToast(error.status)) {
        toast.error(titleFor(error.status), messageFor(error));
      }
      // Rethrown either way: the caller still decides what to render inline.
      return throwError(() => error);
    }),
  );
};

/**
 * 401 is deliberately NOT toasted, and the reason is the mandated interceptor
 * order.
 *
 * `withInterceptors([loading, auth, error])` makes `error` the INNERMOST
 * interceptor, so on the way back it observes the raw backend outcome BEFORE
 * the auth interceptor gets its chance to refresh and retry. Toasting a 401
 * here would therefore fire "your session has ended" on every routine token
 * expiry - a second before the retried request lands with the data. The
 * retried request passes through here too, so the toast could not even be
 * withdrawn.
 *
 * 401 is owned end to end by the auth interceptor instead: it either heals it
 * invisibly, or calls forceLogout() and puts the user on /login, which states
 * the situation far better than a self-dismissing toast. `messageFor` keeps
 * its 401 branch for any caller that wants the sentence.
 */
function shouldToast(status: number): boolean {
  return status !== 401;
}

function titleFor(status: number): string {
  if (status === 0) return 'No connection';
  if (status === 401) return 'Session ended';
  if (status === 403) return 'Not permitted';
  if (status === 404) return 'Not found';
  if (status === 429) return 'Too many attempts';
  if (status >= 500) return 'Server error';
  return 'Request failed';
}

function messageFor(error: HttpErrorResponse): string {
  const status = error.status;

  // status 0 is the "could not even reach the server" case: offline, DNS,
  // blocked by a proxy, or a CORS preflight that failed. The browser gives us
  // nothing more specific, on purpose.
  if (status === 0) {
    return 'The server could not be reached. Check the network connection and try again.';
  }

  if (status === 400) {
    return serverMessage(error) ?? 'Some values were rejected. Review the form and try again.';
  }

  if (status === 401) {
    return 'Your session has ended. Sign in again to continue.';
  }

  if (status === 403) {
    return serverMessage(error) ?? 'Your role does not permit this action.';
  }

  if (status === 404) {
    return serverMessage(error) ?? 'That record no longer exists. It may have been deleted.';
  }

  if (status === 429) {
    const retryAfter = error.headers.get(RETRY_AFTER_HEADER);
    const seconds = Number(retryAfter);
    return Number.isFinite(seconds) && seconds > 0
      ? `Too many attempts. Try again in ${Math.ceil(seconds)} seconds.`
      : 'Too many attempts. Wait a moment and try again.';
  }

  if (status >= 500) {
    // A 500 never carries the exception message - it is logged server-side
    // against a correlation id instead. Surfacing that id is the difference
    // between "something went wrong" and a support ticket someone can action.
    const correlationId = error.headers.get(CORRELATION_ID_HEADER);
    return correlationId
      ? `${API_MESSAGES.failure} Reference ${correlationId}.`
      : API_MESSAGES.failure;
  }

  return serverMessage(error) ?? API_MESSAGES.failure;
}

/** The envelope message from an error body, when the middleware sent one. */
function serverMessage(error: HttpErrorResponse): string | null {
  const body: unknown = error.error;
  if (isApiResponse(body) && typeof body.message === 'string' && body.message.trim()) {
    return body.message;
  }
  return null;
}
